import React, { useEffect, useRef } from "react";
import StyledDiv from "../../../../StyledDiv/StyledDiv";
import ImageResponsive from "../../../../Image/ImageResponsive";
import { responsiveMap } from "../../../../Grid/_utils/responsiveObserve";
import styled, { css } from "styled-components";
import useOnScreen from "../../../../../Hooks/useOnScreen";
import { lazyGsap } from "../../../../../lib/gsap/lazyGsap";

interface IBoxBackgroundImageProps {
  imageId?: string;
  className?: string;
  bg?: {
    desktop?: string;
    mobile?: string;
  };
}

const _BoxBackgroundImageRoot = styled("div")<{
  $bg?: {
    desktop?: string;
    mobile?: string;
  };
}>(({ $bg }) => {
  const { desktop: desktopBg, mobile: mobileBg } = $bg || {};
  return css`
    background-image: ${desktopBg === "none" ? "none" : `url(${desktopBg})`};
    background-blend-mode: exclusion;
    mix-blend-mode: screen;
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    width: 100%;
    height: 100%;
    transform: translateY(-80px);
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    @media ${responsiveMap.maxLg} {
      top: 10%;
      background-image: ${mobileBg === "none"
        ? "none"
        : `url(${mobileBg || desktopBg})`};
      background-position: 32% 17%;
      background-size: 210%;
      transform: translateY(0);
    }
  `;
});

const Root = styled("div")(() => {
  return css`
    overflow: hidden;
    & .image-product {
      max-width: 808px;
      margin: 0 auto;
      @media ${responsiveMap.mobile} {
        display: flex;
        align-items: center;
      }
    }

    .image-product-wrapper {
      @media ${responsiveMap.mobile} {
        justify-content: center;
        display: flex;
        height: auto;

        transform: translate(20%, 0%) scale(1.3);
      }
    }
  `;
});

const BoxBackgroundImage = (props: IBoxBackgroundImageProps) => {
  const { imageId, className, bg } = props;
  const haloRef = useRef<HTMLDivElement>(null);
  const [containerRef, isOnScreen] = useOnScreen<HTMLDivElement>(
    { root: null, rootMargin: "200px 0px", threshold: 0 },
    { ignoreReverse: true }
  );

  useEffect(() => {
    const container = containerRef.current;
    const halo = haloRef.current;
    if (!container || !halo) return;

    let cancelled = false;
    let cleanup: (() => void) | undefined;

    lazyGsap()
      .then(({ gsap }) => {
        if (cancelled) return;
        const ctx = gsap.context(() => {
          gsap.fromTo(
            halo,
            { opacity: 0, zIndex: -1 },
            {
              opacity: 1,
              zIndex: 2,
              ease: "none",
              scrollTrigger: {
                trigger: container,
                start: "top 60%",
                end: "bottom center",
                scrub: true,
              },
            }
          );
        }, container);
        cleanup = () => ctx.revert();
      })
      .catch(() => {
        /* ignore SSR / load errors */
      });

    return () => {
      cancelled = true;
      cleanup?.();
    };
  }, []);

  return (
    <div ref={containerRef} className={className}>
      {isOnScreen && (
        <StyledDiv
          as={Root}
          styles={{
            position: "relative",
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
          }}
        >
          {/* image */}
          <ImageResponsive
            rootClassName="image-product"
            className="image-product-wrapper"
            desktop={{
              image: {
                id: imageId,
                width: 808,
              },
            }}
            mobile={{
              image: {
                id: imageId,
                quality: 75,
                //   width: 1080,
                //   breakpoints: [750, 960],
                //   aspectRatio: 3 / 4,
              },
            }}
          />

          {/* Halo */}
          <_BoxBackgroundImageRoot ref={haloRef} $bg={bg} />
        </StyledDiv>
      )}
    </div>
  );
};

export default BoxBackgroundImage;
